import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import pickleballIcon from "./pickleball.png";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const SKILL_LEVELS = ["입문", "초급", "중급", "고급"];
const AGE_GROUPS = ["10대", "20대", "30대", "40대", "50대 이상", "60대+"];
const GENDERS = ["남녀 모두", "남성만", "여성만"];

const SEOUL_DISTRICTS = [
  "종로구","중구","용산구","성동구","광진구","동대문구","중랑구","성북구",
  "강북구","도봉구","노원구","은평구","서대문구","마포구","양천구","강서구",
  "구로구","금천구","영등포구","동작구","관악구","서초구","강남구","송파구","강동구",
];

const TODAY = new Date().toISOString().split("T")[0];

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "방금";
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}일 전`;
  return new Date(dateStr).toLocaleDateString("ko-KR");
}

function parseArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

export default function ClubRecruitment({ onHome, parentRefreshKey = 0 }) {
  const { user, activeTeam } = useAuth();

  // 뷰 모드: list | create | detail | edit
  const [view, setView] = useState("list");
  const [detailPost, setDetailPost] = useState(null);
  const [editPost, setEditPost] = useState(null);

  // 목록
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  // 필터
  const [fSport, setFSport] = useState("");
  const [fLevel, setFLevel] = useState("");
  const [fGender, setFGender] = useState("");
  const [fRegion, setFRegion] = useState("");
  const [sortOrder, setSortOrder] = useState("latest"); // "latest" | "upcoming"

  // 폼
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [sportsType, setSportsType] = useState(activeTeam?.sports_type || "");
  const [skillLevels, setSkillLevels] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [gender, setGender] = useState("남녀 모두");
  const [region, setRegion] = useState("");
  const [recruitCount, setRecruitCount] = useState("");
  const [deadline, setDeadline] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // 신청
  const [applyMsg, setApplyMsg] = useState("");
  const [applyAge, setApplyAge] = useState("");
  const [applyCareer, setApplyCareer] = useState("");
  const [applyGender, setApplyGender] = useState("");
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyDone, setApplyDone] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);

  // 삭제
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── 목록 조회 ──
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("club_recruitments")
      .select("*")
      .eq("status", "open")
      .order("created_at", { ascending: false });

    const today = new Date().toISOString().split("T")[0];
    const visible = (data || []).filter(
      (p) => !p.deadline || p.deadline >= today
    );
    setPosts(visible);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts, refreshKey, parentRefreshKey]);

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartY.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) setPullY(Math.min(diff * 0.4, 80));
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (pullY > 50) {
      setRefreshing(true);
      await fetchPosts();
      setRefreshing(false);
    }
    setPullY(0);
    touchStartY.current = 0;
  }, [pullY, fetchPosts]);

  // ── 신청 여부 확인 ──
  useEffect(() => {
    if (!user?.id || !detailPost?.id || view !== "detail") return;
    supabase
      .from("club_applications")
      .select("id")
      .eq("recruitment_id", detailPost.id)
      .eq("applicant_user_id", user.id)
      .maybeSingle()
      .then(({ data }) => setAlreadyApplied(!!data));
  }, [user?.id, detailPost?.id, view]);

  // ── 필터 적용 ──
  const filtered = posts.filter((p) => {
    if (fSport && p.sports_type !== fSport) return false;
    if (fLevel && !parseArray(p.skill_levels).includes(fLevel)) return false;
    if (fGender && p.gender !== fGender) return false;
    if (fRegion && p.region !== fRegion) return false;
    return true;
  });

  // ── 정렬 ──
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sorted = sortOrder === "latest"
    ? [...filtered]
    : [...filtered].sort((a, b) => {
        if (sortOrder === "matchDate") {
          const aDate = a.match_date ? new Date(a.match_date) : null;
          const bDate = b.match_date ? new Date(b.match_date) : null;
          const aFuture = aDate && aDate >= today;
          const bFuture = bDate && bDate >= today;
          if (aFuture && bFuture) return aDate - bDate;
          if (aFuture) return -1;
          if (bFuture) return 1;
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return aDate - bDate;
        }
        // deadline
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline) - new Date(b.deadline);
      });

  // ── 폼 초기화 ──
  const resetForm = () => {
    setTitle(""); setContent(""); setSportsType(activeTeam?.sports_type || "");
    setSkillLevels([]); setAgeGroups([]); setGender("남녀 모두");
    setRegion(""); setRecruitCount(""); setDeadline("");
    setFormError(""); setEditPost(null);
  };

  const openCreate = () => { resetForm(); setView("create"); };
  const openEdit = (post) => {
    setEditPost(post);
    setTitle(post.title || "");
    setContent(post.content || "");
    setSportsType(post.sports_type || "");
    setSkillLevels(parseArray(post.skill_levels));
    setAgeGroups(parseArray(post.age_groups));
    setGender(post.gender || "남녀 모두");
    setRegion(post.region || "");
    setRecruitCount(post.recruit_count != null ? String(post.recruit_count) : "");
    setDeadline(post.deadline || "");
    setFormError("");
    setView("edit");
  };

  const openDetail = (post) => {
    setDetailPost(post);
    setAlreadyApplied(false);
    setApplyDone(false);
    setView("detail");
  };

  // ── 저장 ──
  const handleSave = async () => {
    if (!title.trim()) { setFormError("제목을 입력해주세요"); return; }
    if (!sportsType) { setFormError("종목을 선택해주세요"); return; }
    if (!activeTeam) { setFormError("활성 팀을 먼저 설정해주세요"); return; }
    setSaving(true); setFormError("");

    const payload = {
      title: title.trim(),
      content: content.trim() || null,
      sports_type: sportsType,
      skill_levels: skillLevels.length > 0 ? skillLevels : null,
      age_groups: ageGroups.length > 0 ? ageGroups : null,
      gender: gender,
      region: region || null,
      recruit_count: recruitCount ? parseInt(recruitCount, 10) : null,
      deadline: deadline || null,
    };

    if (editPost) {
      const { error } = await supabase
        .from("club_recruitments")
        .update(payload)
        .eq("id", editPost.id);
      setSaving(false);
      if (error) { setFormError("수정 실패: " + error.message); return; }
    } else {
      const { error } = await supabase.from("club_recruitments").insert([{
        ...payload,
        team_id: activeTeam.id,
        team_name: activeTeam.team_name,
        user_id: user.id,
        status: "open",
      }]);
      setSaving(false);
      if (error) { setFormError("등록 실패: " + error.message); return; }
    }

    resetForm();
    setRefreshKey((k) => k + 1);
    setView("list");
  };

  // ── 삭제 ──
  const handleDelete = async () => {
    if (!detailPost) return;
    setDeleteLoading(true);
    await supabase.from("club_applications").delete().eq("recruitment_id", detailPost.id);
    await supabase.from("club_recruitments").delete().eq("id", detailPost.id);
    setDeleteLoading(false);
    setShowDeleteConfirm(false);
    setRefreshKey((k) => k + 1);
    setView("list");
  };

  // ── 가입 신청 ──
  const handleApply = async () => {
    if (!user?.id) { setApplyError("로그인이 필요합니다"); return; }
    setApplyLoading(true); setApplyError("");

    const { data: existing } = await supabase
      .from("club_applications")
      .select("id")
      .eq("recruitment_id", detailPost.id)
      .eq("applicant_user_id", user.id)
      .maybeSingle();
    if (existing) { setApplyError("이미 신청했어요"); setApplyLoading(false); return; }

    const { error } = await supabase.from("club_applications").insert([{
      recruitment_id: detailPost.id,
      applicant_user_id: user.id,
      applicant_email: user.email || null,
      applicant_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split("@")[0] || "",
      age: applyAge.trim() || null,
      career: applyCareer.trim() || null,
      gender: applyGender || null,
      message: applyMsg.trim() || null,
    }]);

    setApplyLoading(false);
    if (error) { setApplyError("신청 실패: " + error.message); return; }

    // 알림
    await supabase.from("notifications").insert([{
      team_id: detailPost.team_id,
      type: "club_apply",
      title: "📋 회원 가입 신청이 왔어요!",
      body: `${activeTeam.team_name} 팀이 회원 모집글에 신청했어요`,
    }]);

    setApplyDone(true);
    setAlreadyApplied(true);
  };

  // ═══ 작성/수정 폼 ═══
  if (view === "create" || view === "edit") {
    return (
      <div style={st.formWrap}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <button style={st.backBtn} onClick={() => { resetForm(); setView("list"); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{editPost ? "모집글 수정" : "회원 모집글 작성"}</span>
        </div>

        <div style={st.section}>
          <label style={st.label}>제목 <span style={st.req}>*</span></label>
          <input style={st.input} placeholder="모집글 제목" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} />
        </div>

        <div style={st.section}>
          <label style={st.label}>종목 <span style={st.req}>*</span></label>
          <select style={st.input} value={sportsType} onChange={(e) => setSportsType(e.target.value)}>
            <option value="">종목 선택</option>
            {Object.entries(SPORTS).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
        </div>

        <div style={st.section}>
          <label style={st.label}>모집 내용</label>
          <textarea style={{ ...st.input, resize: "none", height: 100, lineHeight: 1.6 }} placeholder="팀 소개, 모집 조건 등" value={content} onChange={(e) => setContent(e.target.value)} maxLength={1000} />
        </div>

        <div style={st.section}>
          <label style={st.label}>실력 레벨 <span style={st.opt}>다중 선택</span></label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SKILL_LEVELS.map((lv) => {
              const sel = skillLevels.includes(lv);
              return <button key={lv} style={{ ...st.chip, background: sel ? "#E24B4A" : "#fff", color: sel ? "#fff" : "#6b7280", borderColor: sel ? "#E24B4A" : "#e5e7eb" }} onClick={() => setSkillLevels(sel ? skillLevels.filter((v) => v !== lv) : [...skillLevels, lv])}>{lv}</button>;
            })}
          </div>
        </div>

        <div style={st.section}>
          <label style={st.label}>모집 연령대 <span style={st.opt}>다중 선택</span></label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {AGE_GROUPS.map((ag) => {
              const sel = ageGroups.includes(ag);
              return <button key={ag} style={{ ...st.chip, background: sel ? "#E24B4A" : "#fff", color: sel ? "#fff" : "#6b7280", borderColor: sel ? "#E24B4A" : "#e5e7eb" }} onClick={() => setAgeGroups(sel ? ageGroups.filter((v) => v !== ag) : [...ageGroups, ag])}>{ag}</button>;
            })}
          </div>
        </div>

        <div style={st.section}>
          <label style={st.label}>모집 성별</label>
          <div style={{ display: "flex", gap: 8 }}>
            {GENDERS.map((g) => (
              <button key={g} style={{ ...st.chip, background: gender === g ? "#E24B4A" : "#fff", color: gender === g ? "#fff" : "#6b7280", borderColor: gender === g ? "#E24B4A" : "#e5e7eb" }} onClick={() => setGender(g)}>{g}</button>
            ))}
          </div>
        </div>

        <div style={st.section}>
          <label style={st.label}>활동 지역</label>
          <select style={st.input} value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">지역 선택</option>
            {SEOUL_DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div style={st.section}>
          <label style={st.label}>모집 인원 <span style={st.opt}>선택</span></label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" min="1" max="99" style={{ ...st.input, width: 80, textAlign: "center" }} placeholder="0" value={recruitCount} onChange={(e) => setRecruitCount(e.target.value)} />
            <span style={{ fontSize: 13, color: "#6b7280" }}>명</span>
          </div>
        </div>

        <div style={st.section}>
          <label style={st.label}>모집 마감일 <span style={st.opt}>선택</span></label>
          <input type="date" style={st.input} value={deadline} min={TODAY} onChange={(e) => setDeadline(e.target.value)} />
        </div>

        {formError && <div style={st.errorBox}>{formError}</div>}

        <button style={{ ...st.submitBtn, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
          {saving ? "저장 중..." : editPost ? "수정 완료" : "모집글 등록하기"}
        </button>
      </div>
    );
  }

  // ═══ 상세보기 ═══
  if (view === "detail" && detailPost) {
    const sport = SPORTS[detailPost.sports_type] || { icon: "🏅", label: detailPost.sports_type };
    const isMyPost = activeTeam?.id === detailPost.team_id;
    const ages = parseArray(detailPost.age_groups);

    return (
      <div style={st.formWrap}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <button style={st.backBtn} onClick={() => setView("list")}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#E24B4A" }}>목록</span>
          </button>
          {isMyPost && (
            <div style={{ display: "flex", gap: 6 }}>
              <button style={st.smallBtn} onClick={() => openEdit(detailPost)}>수정</button>
              <button style={{ ...st.smallBtn, color: "#E24B4A", borderColor: "#fecaca" }} onClick={() => setShowDeleteConfirm(true)}>삭제</button>
            </div>
          )}
        </div>

        {/* 히어로 */}
        <div style={st.heroCard}>
          <div style={{ fontSize: 42, marginBottom: 8 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 42, height: 42, objectFit: "contain" }} />}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 4 }}>{detailPost.title}</div>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>{detailPost.team_name} · {sport.label}</div>
        </div>

        {/* 상세 정보 */}
        <div style={st.infoCard}>
          <InfoRow label="실력 레벨" value={parseArray(detailPost.skill_levels).join(", ") || "무관"} />
          <InfoRow label="모집 성별" value={detailPost.gender || "남녀 모두"} />
          {ages.length > 0 && <InfoRow label="모집 연령대" value={ages.join(", ")} />}
          {detailPost.region && <InfoRow label="활동 지역" value={detailPost.region} />}
          {detailPost.recruit_count != null && <InfoRow label="모집 인원" value={`${detailPost.recruit_count}명`} />}
          {detailPost.deadline && <InfoRow label="모집 마감일" value={detailPost.deadline} />}
        </div>

        {/* 모집 내용 */}
        {detailPost.content && (
          <div style={st.msgCard}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>모집 내용</div>
            <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{detailPost.content}</div>
          </div>
        )}

        {/* 신청 버튼 */}
        {!isMyPost && (
          <div style={{ marginTop: 14 }}>
            {!activeTeam ? (
              <div style={{ textAlign: "center", fontSize: 13, color: "#9ca3af", padding: "10px 0" }}>활성 팀을 설정하면 신청할 수 있어요</div>
            ) : (
              <button
                style={{ ...st.submitBtn, background: alreadyApplied ? "#9ca3af" : "#E24B4A", cursor: alreadyApplied ? "default" : "pointer" }}
                onClick={alreadyApplied ? undefined : () => { setApplyMsg(""); setApplyAge(""); setApplyCareer(""); setApplyGender(""); setApplyError(""); setApplyDone(false); setShowApplyModal(true); }}
                disabled={alreadyApplied}
              >
                {alreadyApplied ? "신청 완료" : "가입 신청하기"}
              </button>
            )}
          </div>
        )}

        {/* 삭제 확인 모달 */}
        {showDeleteConfirm && (
          <div style={st.backdrop} onClick={() => setShowDeleteConfirm(false)}>
            <div style={st.modalBox} onClick={(e) => e.stopPropagation()}>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>모집글을 삭제하시겠어요?</div>
                <div style={{ fontSize: 13, color: "#6b7280" }}>삭제하면 신청 내역도 모두 사라집니다.</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={st.cancelBtn} onClick={() => setShowDeleteConfirm(false)} disabled={deleteLoading}>취소</button>
                <button style={{ ...st.submitBtn, flex: 1, opacity: deleteLoading ? 0.6 : 1 }} onClick={handleDelete} disabled={deleteLoading}>
                  {deleteLoading ? "삭제 중..." : "삭제하기"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 신청 모달 */}
        {showApplyModal && (
          <div style={st.backdrop} onClick={() => setShowApplyModal(false)}>
            <div style={st.modalBox} onClick={(e) => e.stopPropagation()}>
              {applyDone ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>신청 완료!</div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>{detailPost.team_name}에 가입 신청했어요</div>
                  <button style={st.submitBtn} onClick={() => setShowApplyModal(false)}>확인</button>
                </div>
              ) : (
                <>
                  <div style={{ textAlign: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>📋</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 4 }}>가입 신청</div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}><span style={{ color: "#E24B4A", fontWeight: 700 }}>{detailPost.team_name}</span>에 가입 신청합니다</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>나이</div>
                      <input style={{ width: "100%", padding: "10px 12px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} placeholder="예: 30" value={applyAge} onChange={(e) => setApplyAge(e.target.value)} maxLength={10} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>성별</div>
                      <select style={{ width: "100%", padding: "10px 12px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} value={applyGender} onChange={(e) => setApplyGender(e.target.value)}>
                        <option value="">선택</option>
                        <option value="남성">남성</option>
                        <option value="여성">여성</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>구력(경력)</div>
                    <input style={{ width: "100%", padding: "10px 12px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} placeholder="예: 3년" value={applyCareer} onChange={(e) => setApplyCareer(e.target.value)} maxLength={20} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>신청 메시지 <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>선택</span></div>
                    <textarea
                      style={{ width: "100%", padding: "12px 14px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, color: "#111827", outline: "none", resize: "none", lineHeight: 1.6, fontFamily: "inherit", boxSizing: "border-box" }}
                      rows={2}
                      placeholder="자기소개나 가입 동기를 남겨보세요"
                      value={applyMsg}
                      onChange={(e) => setApplyMsg(e.target.value)}
                      maxLength={300}
                    />
                  </div>
                  {applyError && <p style={{ fontSize: 12, color: "#E24B4A", marginBottom: 12 }}>{applyError}</p>}
                  <div style={{ display: "flex", gap: 10 }}>
                    <button style={st.cancelBtn} onClick={() => setShowApplyModal(false)} disabled={applyLoading}>취소</button>
                    <button style={{ ...st.submitBtn, flex: 1, opacity: applyLoading ? 0.6 : 1 }} onClick={handleApply} disabled={applyLoading}>
                      {applyLoading ? "신청 중..." : "신청하기"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══ 목록 ═══
  return (
    <div ref={containerRef} style={{ ...st.listWrap, overflowY: "auto" }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {(pullY > 0 || refreshing) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: refreshing ? 40 : pullY, overflow: "hidden", transition: refreshing ? "height 0.2s" : "none", background: "#f7f8fa" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...st.spinner, width: 20, height: 20, borderWidth: 2 }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{refreshing ? "새로고침 중..." : pullY > 50 ? "놓으면 새로고침" : "당겨서 새로고침"}</span>
          </div>
        </div>
      )}
      {/* 필터 */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <select style={st.filterSelect} value={fSport} onChange={(e) => setFSport(e.target.value)}>
          <option value="">종목 전체</option>
          {Object.entries(SPORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select style={st.filterSelect} value={fLevel} onChange={(e) => setFLevel(e.target.value)}>
          <option value="">레벨 전체</option>
          {SKILL_LEVELS.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
        </select>
        <select style={st.filterSelect} value={fGender} onChange={(e) => setFGender(e.target.value)}>
          <option value="">성별 전체</option>
          {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select style={st.filterSelect} value={fRegion} onChange={(e) => setFRegion(e.target.value)}>
          <option value="">지역 전체</option>
          {SEOUL_DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* 작성 버튼 */}
      {user && (
        <button style={st.createBtn} onClick={openCreate}>
          ✏️ 회원 모집글 작성하기
        </button>
      )}

      {/* 결과 수 + 정렬 */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 13 }}>
          <span style={{ color: "#E24B4A", fontWeight: 700 }}>{filtered.length}</span>
          <span style={{ color: "#9ca3af" }}>개 모집글</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[["latest", "최신순"], ["matchDate", "경기임박순"], ["deadline", "마감임박순"]].map(([key, label]) => (
            <button key={key}
              onClick={() => setSortOrder(key)}
              style={{ padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                background: sortOrder === key ? "#1A1A2E" : "#fff",
                color: sortOrder === key ? "#fff" : "#6b7280",
                border: sortOrder === key ? "1px solid #1A1A2E" : "1px solid #d1d5db",
              }}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* 목록 */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <div style={st.spinner} />
        </div>
      ) : sorted.length === 0 ? (
        <div style={st.emptyBox}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 15, color: "#9ca3af", fontWeight: 600 }}>회원 모집글이 없어요</div>
          <div style={{ fontSize: 13, color: "#c4c9d4", marginTop: 6 }}>첫 번째 모집글을 작성해보세요!</div>
        </div>
      ) : (
        sorted.map((p) => {
          const sport = SPORTS[p.sports_type] || { icon: "🏅", label: p.sports_type };
          const isMyPost = activeTeam?.id === p.team_id;
          return (
            <div key={p.id} style={st.card} onClick={() => openDetail(p)}>
              {/* 팀명 + 종목 */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={st.sportBadge}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 18, height: 18, objectFit: "contain" }} />}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>{p.team_name} · {sport.label}</div>
                </div>
                {isMyPost && <span style={st.myBadge}>내 글</span>}
              </div>
              {/* 레벨 배지 + 마감일 + 신청하기 */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flex: 1 }}>
                  {parseArray(p.skill_levels).length > 0 && <span style={st.tagChip}>{parseArray(p.skill_levels).join(", ")}</span>}
                  {p.region && <span style={st.tagChip}>📍 {p.region}</span>}
                  {p.deadline && <span style={{ ...st.tagChip, color: "#d97706", borderColor: "#fde68a", background: "#fffbeb" }}>~{p.deadline.slice(5)}</span>}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); openDetail(p); }}
                  style={{ padding: "5px 10px", background: "#E8412A", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}
                >
                  신청하기
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f0f1f5" }}>
      <span style={{ fontSize: 13, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

const st = {
  listWrap:    { padding: "14px 20px 20px" },
  formWrap:    { padding: "16px 20px 32px" },
  section:     { marginBottom: 20 },
  label:       { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 },
  req:         { color: "#E24B4A", marginLeft: 2 },
  opt:         { fontSize: 11, color: "#9ca3af", fontWeight: 400, marginLeft: 6 },
  input:       { width: "100%", padding: "11px 13px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  chip:        { padding: "8px 14px", border: "1px solid #e5e7eb", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  submitBtn:   { width: "100%", padding: "14px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  createBtn:   { width: "100%", padding: "14px", background: "#111827", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 10 },
  errorBox:    { padding: "12px 14px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 10, fontSize: 13, color: "#dc2626", marginBottom: 14 },
  card:        { background: "#fff", borderRadius: 12, border: "1px solid #edf0f3", padding: "10px 12px", marginBottom: 6, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  sportBadge:  { width: 36, height: 36, background: "#f7f8fa", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, border: "1px solid #edf0f3", flexShrink: 0 },
  tagChip:     { padding: "3px 10px", background: "#f5f6fa", border: "1px solid #edf0f3", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "#6b7280" },
  myBadge:     { padding: "3px 10px", background: "#E24B4A", color: "#fff", borderRadius: 999, fontSize: 11, fontWeight: 700, flexShrink: 0 },
  filterSelect:{ padding: "7px 10px", border: "1px solid #edf0f3", borderRadius: 10, background: "#fff", fontSize: 12, outline: "none", cursor: "pointer", color: "#6b7280" },
  emptyBox:    { textAlign: "center", padding: "40px 20px", background: "#fff", borderRadius: 16, border: "1px solid #edf0f3" },
  spinner:     { width: 32, height: 32, border: "3px solid #edf0f3", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  heroCard:    { background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", padding: "24px 20px", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 14 },
  infoCard:    { background: "#fff", borderRadius: 14, border: "1.5px solid #E24B4A", padding: "4px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", marginBottom: 14 },
  msgCard:     { background: "#fff", borderRadius: 14, border: "1.5px solid #edf0f3", padding: "16px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", marginBottom: 14 },
  backBtn:     { display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", padding: 0 },
  smallBtn:    { padding: "5px 10px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  backdrop:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, backdropFilter: "blur(4px)" },
  modalBox:    { width: "90%", maxWidth: 360, background: "#fff", borderRadius: 18, padding: "22px 20px 26px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)", maxHeight: "85vh", overflowY: "auto" },
  cancelBtn:   { padding: "13px 20px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
};
